import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { CheckCircle2, XCircle, Wallet, Clock3 } from "lucide-react";
import { toast } from "sonner";
import { logger } from "@/lib/logger";

const fmtINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(n) || 0);

const decideDialogTitle = (status) => {
  if (status === "approved") return "Approve request";
  if (status === "rejected") return "Reject request";
  return "Mark disbursed";
};

const ACCENT_CLS = {
  green: "bg-emerald-50 border-emerald-200 text-emerald-900",
  amber: "bg-amber-50 border-amber-200 text-amber-900",
  blue: "bg-blue-50 border-blue-200 text-blue-900",
};

const STATUS_META = {
  pending:   { bg: "bg-amber-50",   text: "text-amber-700",   label: "Pending",   icon: Clock3 },
  approved:  { bg: "bg-emerald-50", text: "text-emerald-700", label: "Approved",  icon: CheckCircle2 },
  disbursed: { bg: "bg-blue-50",    text: "text-blue-700",    label: "Disbursed", icon: CheckCircle2 },
  rejected:  { bg: "bg-red-50",     text: "text-red-700",     label: "Rejected",  icon: XCircle },
};

export default function Advances() {
  const [statusFilter, setStatusFilter] = useState("pending");
  const [items, setItems] = useState([]);
  const [decision, setDecision] = useState(null); // { req, status }
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    try {
      const params = statusFilter && statusFilter !== "all" ? `?status=${statusFilter}` : "";
      const { data } = await api.get(`/advance-requests${params}`);
      setItems(data);
    } catch (err) {
      logger.error("Failed to load advance requests:", err);
      toast.error(err.response?.data?.detail || "Failed to load requests");
    }
  }, [statusFilter]);
  useEffect(() => { load(); }, [load]);

  const totals = useMemo(() => {
    const t = { pending: 0, approved: 0, disbursed: 0, rejected: 0, amt_pending: 0, amt_approved: 0 };
    for (const r of items) {
      t[r.status] = (t[r.status] || 0) + 1;
      if (r.status === "pending") t.amt_pending += Number(r.amount) || 0;
      if (r.status === "approved" || r.status === "disbursed") t.amt_approved += Number(r.amount) || 0;
    }
    return t;
  }, [items]);

  const openDecide = (req, status) => { setDecision({ req, status }); setNote(""); };

  const submitDecide = async () => {
    if (!decision) return;
    try {
      await api.put(`/advance-requests/${decision.req.id}/decide`, {
        status: decision.status, note: note || null,
      });
      toast.success(`Request ${decision.status}`);
      setDecision(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="label-overline">Compensation</span>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-1">Advance Requests</h1>
          <p className="text-sm text-zinc-500 mt-2">Approve, reject, or mark disbursed. Per-employee advance limits are set from the Staff page.</p>
        </div>
        <div className="flex gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="disbursed">Disbursed</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card label="Pending" value={`${totals.pending} · ${fmtINR(totals.amt_pending)}`} accent="amber" />
        <Card label="Approved" value={`${totals.approved} · ${fmtINR(totals.amt_approved)}`} accent="green" />
        <Card label="Disbursed" value={`${totals.disbursed}`} accent="blue" />
        <Card label="Rejected" value={`${totals.rejected}`} />
      </div>

      <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left text-zinc-500 uppercase text-[10px] tracking-widest">
              <th className="py-3 px-4">Employee</th>
              <th className="py-3 px-4 text-right">Amount</th>
              <th className="py-3 px-4">Reason</th>
              <th className="py-3 px-4">Requested</th>
              <th className="py-3 px-4">Status</th>
              <th className="py-3 px-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => {
              const m = STATUS_META[r.status] || STATUS_META.pending;
              const Icon = m.icon;
              return (
                <tr key={r.id} className="border-b border-zinc-100" data-testid={`advance-row-${r.id}`}>
                  <td className="py-2.5 px-4">
                    <div className="font-medium">{r.user_name}</div>
                    <div className="text-[10px] text-zinc-500 font-mono">{r.employee_id || "—"} · {(r.user_role || "").replace("_", " ")}</div>
                  </td>
                  <td className="py-2.5 px-4 text-right font-mono font-semibold">{fmtINR(r.amount)}</td>
                  <td className="py-2.5 px-4 text-zinc-600 max-w-xs truncate" title={r.reason || ""}>{r.reason || "—"}</td>
                  <td className="py-2.5 px-4 text-zinc-500 text-xs">{new Date(r.created_at).toLocaleString([], { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                  <td className="py-2.5 px-4">
                    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded ${m.bg} ${m.text}`}>
                      <Icon className="w-3 h-3" /> {m.label}
                    </span>
                    {r.decision_note && <div className="text-[10px] text-zinc-500 italic mt-0.5">{r.decision_note}</div>}
                  </td>
                  <td className="py-2.5 px-4 text-right">
                    {r.status === "pending" && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => openDecide(r, "approved")} className="text-emerald-700 border-emerald-200" data-testid={`approve-${r.id}`}>Approve</Button>
                        <Button size="sm" variant="outline" onClick={() => openDecide(r, "rejected")} className="text-red-600 border-red-200 ml-1" data-testid={`reject-${r.id}`}>Reject</Button>
                      </>
                    )}
                    {r.status === "approved" && (
                      <Button size="sm" variant="outline" onClick={() => openDecide(r, "disbursed")} className="text-blue-700 border-blue-200" data-testid={`disburse-${r.id}`}>Mark disbursed</Button>
                    )}
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && (
              <tr><td colSpan={6} className="py-10 text-center text-zinc-500"><Wallet className="w-6 h-6 mx-auto text-zinc-300" /><div className="mt-2">No requests.</div></td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!decision} onOpenChange={(v) => !v && setDecision(null)}>
        <DialogContent data-testid="decide-dialog">
          <DialogHeader>
            <DialogTitle>{decideDialogTitle(decision?.status)}</DialogTitle>
            <DialogDescription>
              {decision?.req && (<>{decision.req.user_name} · {fmtINR(decision.req.amount)} · {decision.req.reason || "no reason given"}</>)}
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>Note (optional)</Label>
            <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. paid via UPI on 25th" data-testid="decide-note" />
          </div>
          <DialogFooter>
            <Button onClick={submitDecide} className={decision?.status === "rejected" ? "bg-red-600 hover:bg-red-700" : "bg-zinc-950 hover:bg-zinc-800"} data-testid="decide-confirm">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Card({ label, value, accent }) {
  const cls = ACCENT_CLS[accent] || "bg-white border-zinc-200";
  return (
    <div className={`border rounded-md p-4 ${cls}`}>
      <div className="label-overline">{label}</div>
      <div className="text-xl font-bold font-display mt-1 font-mono">{value}</div>
    </div>
  );
}
